/**
 * Module dependencies.
 */

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from 'app/components/ui/alert-dialog';
import {
  AdminUser,
  useDeleteUser,
  useImpersonateUser,
  useUpdateUserPassword,
  useUsers
} from 'app/hooks/requests/use-admin-users';
import { Avatar } from 'app/components/ui/avatar';
import { Button } from 'app/components/ui/button';
import { KeyRound, Pencil, ShieldCheck, Trash2, VenetianMask } from 'lucide-react';
import { Navigate, useNavigate } from 'react-router-dom';
import { PasswordDialog } from 'app/components/pages/settings/profile/password-dialog';
import { Spinner } from 'app/components/ui/spinner';
import { Title } from 'app/components/atoms/title/title';
import { appRoutes } from 'app/routes';
import { generatePath } from 'react-router-dom';
import moment from 'moment';
import { useMe } from 'app/hooks/requests/use-me';
import { useToast } from 'app/components/providers/toast-provider/toast-provider';
import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';

/**
 * Resolve initials + the accepted auth methods label.
 */

function initials(user: AdminUser): string {
  const source = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email || 'U';

  return source
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join('');
}

function authMethods(user: AdminUser): string {
  return Array.from(new Set([user.auth, user.hasPassword ? 'password' : null].filter(Boolean)))
    .map(method => (method as string).charAt(0).toUpperCase() + (method as string).slice(1))
    .join(', ');
}

/**
 * Export `AdministrationList` component.
 */

export function AdministrationList(): JSX.Element {
  const [translate] = useTranslation();
  const navigate = useNavigate();
  const { success, error } = useToast();
  const { data: me } = useMe();
  const { data: users, isLoading } = useUsers();

  const [passwordUserId, setPasswordUserId] = useState<number | null>(null);
  const [removeTarget, setRemoveTarget] = useState<AdminUser | null>(null);

  const updatePassword = useUpdateUserPassword(passwordUserId ?? undefined);
  const deleteUser = useDeleteUser();
  const impersonate = useImpersonateUser();

  const onImpersonate = async (id: number) => {
    await impersonate.mutateAsync(id);

    // Full reload so the whole app picks up the impersonated session.
    window.location.href = appRoutes.dashboard.home.base;
  };

  if (me && !me.isAdmin) {
    return <Navigate replace to={appRoutes.dashboard.home.base} />;
  }

  const openDetail = (id: number) =>
    navigate(generatePath(appRoutes.dashboard.administration.user, { userId: String(id) }));

  const confirmRemove = async () => {
    if (!removeTarget) {
      return;
    }

    try {
      await deleteUser.mutateAsync(removeTarget.id);

      success({ message: translate('administration.removed') });
    } catch {
      error({ message: translate('administration.removeFailed') });
    } finally {
      setRemoveTarget(null);
    }
  };

  return (
    <div className={'space-y-5'}>
      <Title>{translate('administration.title')}</Title>

      <div className={'overflow-hidden rounded-xl border border-border bg-card shadow-sm'}>
        {isLoading ? (
          <div className={'grid place-items-center py-16'}>
            <Spinner />
          </div>
        ) : (
          <>
            <div
              className={
                'hidden items-center gap-4 border-b border-border px-5 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70 sm:flex'
              }
            >
              <div className={'w-14 shrink-0'}>{translate('administration.columns.id')}</div>

              <div className={'min-w-0 flex-1'}>{translate('administration.columns.user')}</div>

              <div className={'hidden w-28 shrink-0 md:block'}>{translate('settings.profile.items.authMethod')}</div>

              <div className={'w-20 shrink-0 text-center'}>{translate('administration.accounts')}</div>

              <div className={'hidden w-40 shrink-0 lg:block'}>{translate('administration.lastAccess')}</div>

              <div className={'w-[156px] shrink-0'} />
            </div>

            <div className={'divide-y divide-border'}>
              {(users ?? []).map(user => (
                <div className={'flex items-center gap-4 px-5 py-3'} key={user.id}>
                  <div
                    className={'hidden w-14 shrink-0 text-xs font-medium tabular-nums text-muted-foreground sm:block'}
                  >
                    {`#${user.id}`}
                  </div>

                  <div className={'flex min-w-0 flex-1 items-center gap-3'}>
                    <Avatar
                      className={'h-10 w-10 shrink-0 text-sm'}
                      initials={initials(user)}
                      src={user.profilePhotoUrl}
                    />

                    <div className={'min-w-0'}>
                      <div className={'flex items-center gap-2'}>
                        <span className={'truncate text-sm font-medium text-foreground'}>
                          {[user.firstName, user.lastName].filter(Boolean).join(' ') || user.email}
                        </span>

                        {!!user.isAdmin && (
                          <span
                            className={
                              'inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary'
                            }
                          >
                            <ShieldCheck className={'h-3 w-3'} /> {translate('administration.admin')}
                          </span>
                        )}
                      </div>

                      <div className={'truncate text-xs text-muted-foreground'}>{user.email}</div>
                    </div>
                  </div>

                  <div className={'hidden w-28 shrink-0 text-xs text-muted-foreground md:block'}>
                    {authMethods(user)}
                  </div>

                  <div className={'w-20 shrink-0 text-center text-sm font-medium text-foreground'}>
                    {user.bankAccountsCount ?? 0}
                  </div>

                  <div className={'hidden w-40 shrink-0 text-xs text-muted-foreground lg:block'}>
                    {user.lastAccessAt
                      ? moment(user.lastAccessAt).format('DD MMM YYYY, HH:mm')
                      : translate('administration.never')}
                  </div>

                  <div className={'flex shrink-0 items-center gap-1'}>
                    <Button
                      aria-label={translate('administration.impersonate')}
                      disabled={user.id === me?.id}
                      onClick={() => onImpersonate(user.id)}
                      size={'icon'}
                      title={translate('administration.impersonate')}
                      variant={'ghost'}
                    >
                      <VenetianMask className={'h-4 w-4'} />
                    </Button>

                    <Button
                      aria-label={translate('common.actions.edit')}
                      onClick={() => openDetail(user.id)}
                      size={'icon'}
                      variant={'ghost'}
                    >
                      <Pencil className={'h-4 w-4'} />
                    </Button>

                    <Button
                      aria-label={translate('settings.profile.security.reset')}
                      onClick={() => setPasswordUserId(user.id)}
                      size={'icon'}
                      variant={'ghost'}
                    >
                      <KeyRound className={'h-4 w-4'} />
                    </Button>

                    <Button
                      aria-label={translate('common.actions.remove')}
                      className={'text-destructive hover:text-destructive'}
                      disabled={user.id === me?.id}
                      onClick={() => setRemoveTarget(user)}
                      size={'icon'}
                      variant={'ghost'}
                    >
                      <Trash2 className={'h-4 w-4'} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <PasswordDialog
        onOpenChange={value => !value && setPasswordUserId(null)}
        onSave={password => updatePassword.mutateAsync(password)}
        open={passwordUserId != null}
      />

      <AlertDialog onOpenChange={value => !value && setRemoveTarget(null)} open={!!removeTarget}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{translate('administration.remove.title')}</AlertDialogTitle>

            <AlertDialogDescription>
              {translate('administration.remove.description', {
                name: removeTarget
                  ? [removeTarget.firstName, removeTarget.lastName].filter(Boolean).join(' ') || removeTarget.email
                  : ''
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>{translate('common.actions.cancel')}</AlertDialogCancel>

            <AlertDialogAction
              className={'bg-destructive text-destructive-foreground hover:bg-destructive/90'}
              onClick={confirmRemove}
            >
              {translate('common.actions.remove')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
